import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { productWikiServices } from 'services/productWikiServices';
import {
  MoleculeInfo,
  StatData,
  StrengthData,
  ANDAData,
  MoleculeMarketInsightsData,
  InsightData,
  CotDataItem,
} from './MoleculeSearch.type';

/**
 * Format currency value to match API formatting style (e.g., $2.4M, $751.0K)
 */
const formatCurrencyValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0';
  }

  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    return `$${(absValue / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `$${(absValue / 1000).toFixed(1)}K`;
  } else {
    return `$${absValue.toFixed(2)}`;
  }
};

/**
 * Transform API market insights data to InsightData array for display
 */
const transformMarketInsightsToCards = (data: MoleculeMarketInsightsData): InsightData[] => {
  // Validate that data exists
  if (!data) {
    throw new Error('Market insights data is null or undefined');
  }

  const cards: InsightData[] = [
    {
      title: 'Total Addressable Market',
      value: data.totalAddressableMarket?.formatted || 'N/A',
      description: data.totalAddressableMarket?.description || '',
      icon: 'fas fa-dollar-sign',
      iconColor: 'var(--success-color)',
    },
    {
      title: 'Market Concentration',
      value: data.marketConcentration?.formatted || 'N/A',
      description: data.marketConcentration?.description || '',
      icon: 'fas fa-users',
      iconColor: 'var(--accent-color)',
    },
    {
      title: 'Price Trends',
      value: data.priceTrends?.formatted || 'N/A',
      description: data.priceTrends?.description || '',
      icon: 'fas fa-chart-line',
      iconColor:
        data.priceTrends?.direction === 'declining' ||
        data.priceTrends?.direction === 'slight_decline'
          ? 'var(--error-color)'
          : data.priceTrends?.direction === 'increasing' ||
              data.priceTrends?.direction === 'slight_increase'
            ? 'var(--success-color)'
            : 'var(--warning-color)',
    },
    {
      title: 'Entry Timeline',
      value: data.entryTimeline?.formatted || 'N/A',
      description: data.entryTimeline?.description || '',
      icon: 'fas fa-clock',
      iconColor: 'var(--secondary-color)',
    },
  ];

  // Add Addressable Market by COT card if COT data is available
  try {
    const tamByCot = data.totalAddressableMarket?.byCot;
    const gsmsByCot = data.gsmsMarket?.byCot;
    
    if (
      tamByCot &&
      gsmsByCot &&
      Object.keys(tamByCot).length > 0 &&
      Object.keys(gsmsByCot).length > 0
    ) {
      const cotData: CotDataItem[] = [];
      const cotLabels: Record<string, string> = {
        tmop: 'TMOP',
        cmop: 'CMOP',
        mtf: 'MTF',
        vaFacility: 'VA Facility',
      };

      // Calculate addressable market for each COT
      Object.keys(cotLabels).forEach((cotKey) => {
        try {
          const tamCot = data.totalAddressableMarket?.byCot?.[cotKey as keyof typeof data.totalAddressableMarket.byCot];
          const gsmsCot = data.gsmsMarket?.byCot?.[cotKey as keyof typeof data.gsmsMarket.byCot];

          if (tamCot) {
            const tamValue = tamCot.value ?? 0;
            const gsmsValue = gsmsCot?.value ?? 0;
            const addressableValue = tamValue - gsmsValue;
            const formattedValue = formatCurrencyValue(addressableValue);

            cotData.push({
              label: cotLabels[cotKey],
              value: formattedValue,
            });
          }
        } catch (cotError) {
          // Skip this COT if there's an error processing it
          console.warn(`Error processing COT ${cotKey}:`, cotError);
        }
      });

      // Only add the card if we have COT data
      if (cotData.length > 0) {
        cards.push({
          title: 'Addressable market by COT',
          value: '', // Not used when cotData is present
          description: 'Remaining addressable market opportunity by Class of Trade',
          icon: 'fas fa-chart-pie',
          iconColor: 'var(--primary-color)',
          cotData,
        });
      }
    }
  } catch (cotCardError) {
    // If COT card generation fails, continue without it
    console.warn('Error generating COT card:', cotCardError);
  }

  return cards;
};

export const useMoleculeSearchLogic = () => {
  const [searchParams] = useSearchParams();
  const { moleculeName } = useParams<{ moleculeName?: string }>();

  // Molecule info and stats state (loaded first)
  const [moleculeInfo, setMoleculeInfo] = useState<MoleculeInfo | null>(null);
  const [stats, setStats] = useState<StatData[]>([]);
  const [moleculeInfoLoading, setMoleculeInfoLoading] = useState(true);
  const [moleculeInfoError, setMoleculeInfoError] = useState<string | null>(null);

  // Strengths state
  const [strengths, setStrengths] = useState<StrengthData[]>([]);
  const [strengthsLoading, setStrengthsLoading] = useState(true);
  const [strengthsError, setStrengthsError] = useState<string | null>(null);

  // ANDA data state
  const [andaData, setAndaData] = useState<ANDAData[]>([]);
  const [andaDataLoading, setAndaDataLoading] = useState(true);
  const [andaDataError, setAndaDataError] = useState<string | null>(null);

  // Market insights state
  const [marketInsights, setMarketInsights] = useState<InsightData[] | null>(null);
  const [marketInsightsLoading, setMarketInsightsLoading] = useState(true);
  const [marketInsightsError, setMarketInsightsError] = useState<string | null>(null);

  // AbortController ref for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Fetch molecule info and stats (first API call)
   */
  const fetchMoleculeInfoStats = useCallback(
    async (item: string, type: 'molecule' | 'concept' | 'ndc', signal: AbortSignal) => {
      setMoleculeInfoLoading(true);
      setMoleculeInfoError(null);

      try {
        const response = await productWikiServices.getMoleculeInfoStats(item, type, signal);

        if (response.data) {
          setMoleculeInfo(response.data.moleculeInfo || null);
          setStats(response.data.stats || []);
          return response.data.moleculeInfo?.name || null;
        } else {
          setMoleculeInfoError('Failed to load molecule info');
          return null;
        }
      } catch (error) {
        const err = error as { name?: string; code?: string };
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
          return null;
        }
        setMoleculeInfoError('Failed to load molecule info. Please try again.');
        return null;
      } finally {
        setMoleculeInfoLoading(false);
      }
    },
    []
  );

  /**
   * Fetch strengths data
   */
  const fetchStrengths = useCallback(
    async (item: string, type: 'molecule' | 'concept' | 'ndc', signal: AbortSignal) => {
      setStrengthsLoading(true);
      setStrengthsError(null);

      try {
        const response = await productWikiServices.getMoleculeStrengths(item, type, signal);

        if (response.data) {
          setStrengths(response.data.strengths || []);
        } else {
          setStrengthsError('Failed to load strengths data');
        }
      } catch (error) {
        const err = error as { name?: string; code?: string };
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
          return;
        }
        setStrengthsError('Failed to load strengths data. Please try again.');
      } finally {
        setStrengthsLoading(false);
      }
    },
    []
  );

  /**
   * Fetch ANDA data
   */
  const fetchAndaData = useCallback(
    async (item: string, type: 'molecule' | 'concept' | 'ndc', signal: AbortSignal) => {
      setAndaDataLoading(true);
      setAndaDataError(null);

      try {
        const response = await productWikiServices.getMoleculeAndaData(item, type, signal);

        if (response.data) {
          setAndaData(response.data.andaData || []);
        } else {
          setAndaDataError('Failed to load ANDA data');
        }
      } catch (error) {
        const err = error as { name?: string; code?: string };
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
          return;
        }
        setAndaDataError('Failed to load ANDA data. Please try again.');
      } finally {
        setAndaDataLoading(false);
      }
    },
    []
  );

  /**
   * Fetch market insights data for the molecule
   */
  const fetchMarketInsights = useCallback(async (moleculeNameForInsights: string, signal: AbortSignal) => {
    setMarketInsightsLoading(true);
    setMarketInsightsError(null);

    try {
      const response = await productWikiServices.getMoleculeMarketInsights(
        moleculeNameForInsights,
        signal
      );

      if (response.data.success && response.data.data) {
        try {
          // Log the response structure for debugging
          const transformedInsights = transformMarketInsightsToCards(response.data.data);
          setMarketInsights(transformedInsights);
        } catch (transformError) {
          const err = transformError as Error;
          console.error('Error transforming market insights:', err);
          console.error('Transform error stack:', err?.stack);
          console.error('Response data that failed:', response.data.data);
          setMarketInsightsError('Failed to process market insights data. Please try again.');
        }
      } else {
        console.warn('Market insights API returned unsuccessful response:', response.data);
        setMarketInsightsError(response.data.error || 'Failed to load market insights');
      }
    } catch (error) {
      const err = error as { name?: string; code?: string };
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Error fetching market insights:', error);
      setMarketInsightsError('Failed to load market insights. Please try again.');
    } finally {
      setMarketInsightsLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchAllData = async () => {
      // Cancel any existing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // Reset all states
      setMoleculeInfo(null);
      setStats([]);
      setStrengths([]);
      setAndaData([]);
      setMarketInsights(null);
      setMoleculeInfoError(null);
      setStrengthsError(null);
      setAndaDataError(null);
      setMarketInsightsError(null);

      // Get search parameters from URL
      const itemFromQuery = searchParams.get('item') || moleculeName;
      const typeFromQuery = searchParams.get('type') as 'molecule' | 'concept' | 'ndc' | null;

      // Validate parameters
      if (!itemFromQuery || !typeFromQuery) {
        setMoleculeInfoLoading(false);
        setStrengthsLoading(false);
        setAndaDataLoading(false);
        setMarketInsightsLoading(false);
        return;
      }

      // Fire all 4 API calls simultaneously when the component mounts
      await Promise.all([
        fetchMoleculeInfoStats(itemFromQuery, typeFromQuery, signal),
        fetchStrengths(itemFromQuery, typeFromQuery, signal),
        fetchAndaData(itemFromQuery, typeFromQuery, signal),
        // Market insights API expects molecule name; use search item (works when type is 'molecule')
        fetchMarketInsights(itemFromQuery, signal),
      ]);
    };

    fetchAllData();

    // Cleanup on unmount or when dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchParams, moleculeName, fetchMoleculeInfoStats, fetchStrengths, fetchAndaData, fetchMarketInsights]);

  // Compute combined loading state for initial page load
  const loading = moleculeInfoLoading;

  // Compute combined molecule data for backward compatibility
  const moleculeData = moleculeInfo
    ? {
        moleculeInfo,
        stats,
        strengths,
        andaData,
        insights: marketInsights || [],
      }
    : null;

  return {
    // Combined data (for backward compatibility)
    moleculeData,
    loading,

    // Molecule info and stats
    moleculeInfo,
    stats,
    moleculeInfoLoading,
    moleculeInfoError,

    // Strengths
    strengths,
    strengthsLoading,
    strengthsError,

    // ANDA data
    andaData,
    andaDataLoading,
    andaDataError,

    // Market insights
    marketInsights,
    marketInsightsLoading,
    marketInsightsError,
  };
};
